import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import LessonsChartSection from "./lessonsChartSection";
import FreeplayChartSection from "./freeplayChartSection";
import AssignmentData from "./AssignmentData";
import { useFetchLessonsNFreeplayQuery } from "app/services/reports";
import { useEffect, useState } from "react";
import { dataValue } from "components/BarsGroupChart";
import { useAppDispatch } from "app/hooks";
import { assignmentUsageLoadingSet } from "features/Reports";

const customStyles = {
  root: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    padding: "20px 0",
    "@media print": {
      padding: 0,
      width: "100%",
    },
  },
  header: {
    color: "rgba(60, 180, 229, 1)",
    fontSize: "22px",
    fontWeight: "bold",
    "@media print": {
      padding: 0,
      pageBreakBefore: "avoid",
      pageBreakAfter: "avoid",
    },
  },
  statistics: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& > span": {
      padding: "10px 30px",
      fontWeight: "bold",
      fontSize: "14px",
    },
  },
};

interface IProps {}
const LessonsAndFreeplay = (props: IProps) => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const [lessonsData, setLessonsData] = useState<dataValue[]>([]);
  const [freeplayData, setFreeplayData] = useState<dataValue[]>([]);
  const [teachersData, setTeachersData] = useState<(string | number)[][]>([]);
  const { data, isLoading: isAssignmentUsageLoading } =
    useFetchLessonsNFreeplayQuery();

  useEffect(() => {
    dispatch(assignmentUsageLoadingSet(isAssignmentUsageLoading));
  }, [isAssignmentUsageLoading]);

  useEffect(() => {
    if (data?._entity) {
      const { _months, _teachersAssignment } = data._entity;

      const monthsData = _months.reduce<{
        lessonsRecords: dataValue[];
        freeplayRecords: dataValue[];
      }>(
        (acc, record) => {
          const lessonsRec = {
            [t("lessonsAssigned")]: record._lessonsAssigned,
            [t("lessonsCompleted")]: record._lessonsCompleted,
            selectedItem: t(record._month),
            xAxisLabel: t(record._month),
            title: t(record._month),
          };
          acc.lessonsRecords.push(lessonsRec);

          const freeplayRec = {
            [t("freeplayAssigned")]: record._freePlayAssigned,
            [t("freeplayCompleted")]: record._freePlayCompleted,
            selectedItem: t(record._month),
            xAxisLabel: t(record._month),
            title: t(record._month),
          };
          acc.freeplayRecords.push(freeplayRec);

          return acc;
        },
        {
          lessonsRecords: [],
          freeplayRecords: [],
        }
      );
      setLessonsData(monthsData.lessonsRecords);
      setFreeplayData(monthsData.freeplayRecords);

      const teachersAssignmentRecords = _teachersAssignment.reduce<
        (string | number)[][]
      >((acc, assignment) => {
        const {
          _teacherName,
          _lessonsAssigned,
          _lessonsCompleted,
          _freePlayAssigned,
          _freePlayCompleted,
        } = assignment;
        const record = [
          _teacherName,
          _lessonsAssigned,
          _lessonsCompleted,
          _freePlayAssigned,
          _freePlayCompleted,
        ];
        acc.push(record);
        return acc;
      }, []);
      setTeachersData(teachersAssignmentRecords);
    }
  }, [data, i18n.dir()]);

  return (
    <Typography component="div" sx={customStyles.root}>
      <Typography component="h1" sx={customStyles.header}>
        {t("Lessons & Freeplay Usage Report")}
      </Typography>
      <LessonsChartSection data={lessonsData} />
      <FreeplayChartSection data={freeplayData} />
      <AssignmentData data={teachersData} />
    </Typography>
  );
};

export default LessonsAndFreeplay;
